import { Component, Input, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LuxonDateAdapter, MAT_LUXON_DATE_FORMATS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { TranslocoModule } from '@jsverse/transloco';
import { DateTime } from 'luxon';

import { CapitalizePipe } from '@hmax-order/frontend/utils';
import { ErrorMessage } from '../../../interfaces';
import { FirstErrorMessagePipe } from '../../pipes';

@Component({
  selector: 'hmax-input-date',
  standalone: true,
  templateUrl: './input-date.component.html',
  styleUrls: ['./input-date.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CapitalizePipe,
    FirstErrorMessagePipe,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (component: InputDateComponent) => component.getDateFormats(),
      deps: [InputDateComponent],
    },
  ],
})
export class InputDateComponent implements OnInit {
  @Input() label!: string;
  @Input() placeholder!: string;
  @Input() max!: Date;
  @Input() min!: Date;
  @Input() formGroup!: FormGroup;
  @Input() controlName!: string;
  @Input() startView: 'month' | 'year' | 'multi-year' = 'month';
  @Input() autocomplete = 'new-date';
  @Input() maxLength = 255;
  @Input() disableManualInput = false;
  /** Evita reserva fixa de espaço abaixo do campo quando não há erro/hint */
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  /**
   * Calendário em modal centralizado. Continua ligado por default para não mudar os consumidores
   * que já existiam; filtro de lista no painel usa `false`, que abre o calendário ancorado no campo.
   */
  @Input() touchUi = true;

  errorMessages: ErrorMessage[] = [
    { error: 'required', message: 'required-field' },
    { error: 'matDatepickerParse', message: 'invalid-date' },
    { error: 'birthDate', message: 'must-be-of-legal-age' },
    { error: 'checkoutLowerThanCheckin', message: 'check-out-after-check-in' },
    { error: 'minDateToday', message: 'min-date-today' },
    { error: 'afterDateRegister', message: 'after-date-register' },
  ];

  get formControl(): AbstractControl {
    return this.formGroup.controls[this.controlName];
  }

  ngOnInit(): void {
    // Carregar controlName com o label se não houver controlName definido
    this.controlName = this.controlName ?? this.label;
  }

  setMonthAndYear(normalizedMonthAndYear: DateTime, datepicker: MatDatepicker<DateTime>): void {
    if (this.startView !== 'multi-year') {
      return;
    }

    const ctrlValue = this.formControl?.value
      ? DateTime.fromISO(this.formControl.value.toISO()) // Garante que o valor seja um DateTime
      : DateTime.now(); // Usa a data atual se não houver valor definido

    const updatedDate = ctrlValue.set({ month: normalizedMonthAndYear.month, year: normalizedMonthAndYear.year });

    this.formControl?.setValue(updatedDate);
    datepicker.close();
  }

  /**
   * Permitir apenas números e separadores de data (/) no campo de entrada.
   */
  onKeyDown(event: KeyboardEvent): void {
    if (!/^[0-9/]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /** Retorna o formato de data com base no startView */
  getDateFormats(): MatDateFormats {
    if (this.startView === 'multi-year') {
      return {
        parse: {
          dateInput: 'MM/yy',
        },
        display: {
          dateInput: 'MM/yy',
          monthYearLabel: 'MMM yyyy',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM yyyy',
        },
      };
    }

    return MAT_LUXON_DATE_FORMATS; // Formato padrão
  }
}
